import { CreateTrayItemInput } from 'common/types'
import { TrayItem } from '../../AddTray.types'
import {
  TraySubmissionResult,
  TraySubmissionSummary,
  TrayVerificationResult,
  TraySubmissionConfig,
  DEFAULT_SUBMISSION_CONFIG,
} from './TraySubmission.types'

export class TraySubmissionService {
  private config: TraySubmissionConfig

  constructor(config: Partial<TraySubmissionConfig> = {}) {
    this.config = { ...DEFAULT_SUBMISSION_CONFIG, ...config }
  }

  /**
   * Delays execution for a specified amount of time
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Verifies if a tray exists in the database by its barcode
   */
  async verifyTrayExists(
    barcode: string,
    getTrayItem: (variables: { variables: { barcode: string } }) => Promise<any>
  ): Promise<TrayVerificationResult> {
    try {
      const { data } = await getTrayItem({ variables: { barcode } })
      const trayData = data?.getTrayItemByBarcode

      return {
        barcode,
        exists: Boolean(trayData?.id),
        trayData,
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error verifying tray ${barcode}:`, error)
      return {
        barcode,
        exists: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Attempts to create a single tray with retry logic
   */
  async submitSingleTray(
    tray: TrayItem,
    createTrayItemInput: (tray: TrayItem) => CreateTrayItemInput,
    createTrayItem: (input: CreateTrayItemInput) => Promise<any>,
    getTrayItem: (variables: { variables: { barcode: string } }) => Promise<any>
  ): Promise<TraySubmissionResult> {
    const result: TraySubmissionResult = {
      trayId: tray.id,
      barcode: tray.trayBarcode,
      description: tray.trayDescription,
      success: false,
      attempts: 0,
    }

    for (let attempt = 1; attempt <= this.config.maxRetries; attempt++) {
      result.attempts = attempt

      try {
        // Create the tray
        const itemInput = createTrayItemInput(tray)
        const createResult = await createTrayItem(itemInput)

        if (!createResult?.id) {
          throw new Error('No tray ID returned from creation')
        }

        result.createdTrayId = createResult.id

        // Wait before verification to allow database propagation
        await this.delay(this.config.verificationDelay)

        // Verify the tray was actually created
        const verification = await this.verifyTrayExists(
          tray.trayBarcode,
          getTrayItem
        )

        if (verification.exists) {
          result.success = true
          return result
        } else {
          throw new Error(
            `Tray created but not found in database: ${
              verification.error || 'Unknown verification error'
            }`
          )
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        // eslint-disable-next-line no-console
        console.error(
          `Attempt ${attempt} failed for tray ${tray.trayBarcode}:`,
          errorMessage
        )

        result.error = errorMessage

        // If this isn't the last attempt, wait before retrying
        if (attempt < this.config.maxRetries) {
          await this.delay(this.config.retryDelay * attempt) // Exponential backoff
        }
      }
    }

    // eslint-disable-next-line no-console
    console.error(
      `All ${this.config.maxRetries} attempts failed for tray ${tray.trayBarcode}`
    )
    return result
  }

  /**
   * Submits multiple trays and returns a comprehensive summary
   */
  async submitMultipleTrays(
    trays: TrayItem[],
    createTrayItemInput: (tray: TrayItem) => CreateTrayItemInput,
    createTrayItem: (input: CreateTrayItemInput) => Promise<any>,
    getTrayItem: (variables: { variables: { barcode: string } }) => Promise<any>
  ): Promise<TraySubmissionSummary> {
    const results: TraySubmissionResult[] = []

    // Submit trays sequentially to avoid overwhelming the database
    for (const tray of trays) {
      const result = await this.submitSingleTray(
        tray,
        createTrayItemInput,
        createTrayItem,
        getTrayItem
      )
      results.push(result)
    }

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => !r.success)

    return {
      successful,
      failed,
      totalTrays: trays.length,
      successCount: successful.length,
      failureCount: failed.length,
    }
  }

  /**
   * Formats the submission summary for user display
   */
  formatSubmissionSummary(summary: TraySubmissionSummary): {
    title: string
    message: string
    variant: 'success' | 'warning' | 'error'
  } {
    const { successCount, failureCount, totalTrays } = summary

    if (failureCount === 0) {
      return {
        title: 'Success!',
        message: `All ${totalTrays} tray${
          totalTrays > 1 ? 's' : ''
        } submitted successfully.`,
        variant: 'success',
      }
    }

    if (successCount === 0) {
      return {
        title: 'Submission Failed',
        message: `Failed to submit all ${totalTrays} tray${
          totalTrays > 1 ? 's' : ''
        }. Please check the details below and try again.`,
        variant: 'error',
      }
    }

    return {
      title: 'Partial Success',
      message: `${successCount} of ${totalTrays} trays submitted successfully. ${failureCount} tray${
        failureCount > 1 ? 's' : ''
      } failed.`,
      variant: 'warning',
    }
  }
}
